import { CardHeader, Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import React, { memo } from 'react';
import { TotalItemEnum } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/components/KeysIndicators/helpers/constants';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { SimulationTypeEnum } from 'routes/HumanResourcesModule/Simulation/helpers/constants';
import { TotalResultAmounts } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/components/KeysIndicators/helpers/types';
import { formatAmountsNumber } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/dataHelpers';
import { HrGridPaper } from 'routes/HumanResourcesModule/components/HrGridPaper/HrGridPaper';
import { HrGridPaperElement } from 'routes/HumanResourcesModule/components/HrGridPaper/types';
import { SimulationResultCardContent } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/components/KeysIndicators/SimulationResultItem';
import { styled } from '@mui/material/styles';
import StyCard from 'routes/HumanResourcesModule/components/ui/StyCard';
import { TFunction, useTranslation } from 'react-i18next';
import { formatSimulationDateString } from 'routes/HumanResourcesModule/Simulation/helpers/dataHelpers';
type Props = {
    totalAmounts: TotalResultAmounts;
    isLoading: boolean;
    hrSimulation: HrSimulation;
};
const StyHrCard = styled(StyCard)(() => ({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: '1',
    height: '100%',
}));

const getTotalAmountsElements = (
    totalAmounts: TotalResultAmounts,
    isLoading: boolean,
    hrSimulation: HrSimulation,
    t: TFunction<'translation', undefined>,
): HrGridPaperElement[] => {
    const { startDate, type: hrSimulationType } = hrSimulation;
    const startDateYear = parseInt(startDate.slice(0, 4), 10);
    const endDateYear = parseInt(startDate.slice(0, 4), 10);

    const isBudgetProposal =
        hrSimulationType === SimulationTypeEnum.BUDGET_PROPOSAL;

    const previousYearElement: HrGridPaperElement = {
        title: (
            <Typography>
                {t(
                    `humanResources.simulation.keysIndicators.${TotalItemEnum.LAST_YEAR}.subTitle`,
                    { year: startDateYear - 1 },
                )}
            </Typography>
        ),
        children: (
            <Typography variant="h5">
                {formatAmountsNumber(
                    totalAmounts.previousYearRealizedTotalAmount,
                )}
            </Typography>
        ),
        isLoading: isLoading,
    };
    const firstYearElement: HrGridPaperElement = {
        title: (
            <Typography>
                {t(
                    `humanResources.simulation.keysIndicators.${TotalItemEnum.PROJECTION}.subTitle`,
                    { year: startDateYear },
                )}
                <Tooltip
                    title={t(
                        'humanResources.simulation.keysIndicators.simulatedAndRealizedAmountTooltip',
                        {
                            startDate: formatSimulationDateString(startDate),
                            endDate: formatSimulationDateString(
                                `${endDateYear}-12-31`,
                            ),
                        },
                    )}
                    key="button-element"
                >
                    <InfoIcon
                        color="primary"
                        sx={{
                            paddingLeft: '1px',
                            fontSize: 16,
                        }}
                    />
                </Tooltip>
            </Typography>
        ),
        children: (
            <Typography variant="h5">
                {formatAmountsNumber(
                    totalAmounts.currentYearRealizedTotalAmountUntilStartDate +
                        totalAmounts.currentYearSimulatedTotalAmountFromStartDate,
                )}
            </Typography>
        ),
        isLoading: isLoading,
    };
    const lastYearElement: HrGridPaperElement = {
        title: t(
            `humanResources.simulation.keysIndicators.${TotalItemEnum.BUDGET_PROPOSAL}.subTitle`,
            { year: startDateYear + 1 },
        ),
        children: (
            <Typography variant="h5">
                {formatAmountsNumber(totalAmounts.nextYearSimulatedTotalAmount)}
            </Typography>
        ),
        isLoading: isLoading,
    };

    return [
        previousYearElement,
        firstYearElement,
        ...(isBudgetProposal ? [lastYearElement] : []),
    ];
};
const TotalAmount = ({
    totalAmounts,
    hrSimulation,
    isLoading,
}: Props): React.ReactElement => {
    const { t } = useTranslation();

    const elements: HrGridPaperElement[] = getTotalAmountsElements(
        totalAmounts,
        isLoading,
        hrSimulation,
        t,
    );

    return (
        <StyHrCard>
            <CardHeader
                titleTypographyProps={{ variant: 'h5' }}
                title={t('humanResources.simulation.keysIndicators.amount')}
            />
            <SimulationResultCardContent>
                <HrGridPaper elements={elements} />
            </SimulationResultCardContent>
        </StyHrCard>
    );
};

export default memo(TotalAmount);
